import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buyerActivityAPI } from '../../lib/api';

/**
 * "This buyer is on the page RIGHT NOW."
 *
 * Deliberately NOT a new badge language: the dot is the same rose-500 pulse
 * already used for an unread alert on the Sales Desk, so a salesperson reads it
 * as "something needs you" without being taught anything new.
 *
 * HONEST COPY. This is a fast-refreshing activity feed and nothing else. No
 * camera, no screen sharing, no video of the buyer exists anywhere in this
 * platform — so the words stay "Live activity", never "watch" or "view".
 */

export const LIVE_POLL_MS = 8000;

export const LiveNowBadge = ({ label, className = '', testid = 'live-now-badge' }) => {
    const { t } = useTranslation();
    return (
        <span data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-2 py-0.5
                        text-[10px] font-bold uppercase tracking-wide text-rose-300 ${className}`}>
            <span className="relative flex w-2 h-2 shrink-0">
                <span className="absolute inset-0 rounded-full bg-rose-500 animate-ping opacity-75" />
                <span className="relative w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_0_3px_rgba(244,63,94,0.2)]" />
            </span>
            {label || t('liveActivity.badge', 'Live now')}
        </span>
    );
};

/** Three breathing dots — "the feed is listening", not "the page is stuck". */
export const ListeningDots = ({ className = '' }) => (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid="live-listening-dots" aria-hidden="true">
        {[0, 150, 300].map((d) => (
            <span key={d} className="w-1.5 h-1.5 rounded-full bg-rose-400/80 animate-pulse"
                style={{ animationDelay: `${d}ms`, animationDuration: '1.2s' }} />
        ))}
    </span>
);

/**
 * Which conversations in this org have their buyer on the page right now.
 * ONE call per tick for a whole board, rather than a presence lookup per row.
 */
export function useLiveBuyers(organizationId, enabled = true) {
    const [ids, setIds] = useState(() => new Set());
    const alive = useRef(true);

    const pull = useCallback(async () => {
        if (!organizationId) return;
        try {
            const r = await buyerActivityAPI.live(organizationId);
            if (alive.current) setIds(new Set(r.data?.conversation_ids || []));
        } catch {
            // A presence failure must never blank out the board it decorates.
        }
    }, [organizationId]);

    useEffect(() => {
        alive.current = true;
        if (!enabled || !organizationId) { setIds(new Set()); return () => { alive.current = false; }; }
        pull();
        const iv = setInterval(() => { if (!document.hidden) pull(); }, LIVE_POLL_MS);
        const onVis = () => { if (!document.hidden) pull(); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            alive.current = false;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [enabled, organizationId, pull]);

    return ids;
}

export default LiveNowBadge;
